// © Microsoft Corporation. All rights reserved.

import { classNames } from "@microsoft/fast-web-utilities";
import { ActivityData, User } from "@msnews/social-data-service";
import { activityImages, avatarFallback, close, enums, exclamation_round, openArticlePage, openGuidelinePage, openProfilePage, placeholderImages, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid, TelemetryEventMap } from "@msnews/social-utils";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import React from "react";
import { renderToString } from "react-dom/server";
import { String } from "typescript-string-operations";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { ComponentConnector, FollowInfo, UsersConnector } from "../../Social.props";
import { notificationCardTelemetryContracts, NotificationCardTelemetryObjects } from "./NotificationCard.telemetry";

export interface NotificationCardProps {
    componentConnector: ComponentConnector;
    notification: ActivityData,
    managedClasses?: SocialClassNameContract;
    t: SocialLocalizedStrings;
    usersConnector: UsersConnector;
    users: { [id: string]: FollowInfo}; // dictionary definening
    market: string;
    me: User;
    theme: string;
    mainTelemetryObject: TelemetryObjectBase;
}

export default class NotificationCard extends React.Component<NotificationCardProps> {
    containerRef: HTMLDivElement;
    telemetryObjects: NotificationCardTelemetryObjects;
    private static AchievementTitles;
    private static AchievementMessages;
    isLoaded: boolean;

    constructor(props) {
        super(props);

        // Initialize variables with localized strings.
        NotificationCard.AchievementTitles = {
            Member: props.t.AchievementTitlesMember,
            Commenter: props.t.AchievementTitlesCommenter,
            Networker: props.t.AchievementTitlesNetworker,
            ConversationStarter: props.t.AchievementTitlesConversationStarter
        };

        NotificationCard.AchievementMessages = {
            Member: props.t.AchievementMessagesMember,
            Commenter: props.t.AchievementMessagesCommenter,
            Networker: props.t.AchievementMessagesNetworker,
            ConversationStarter: props.t.AchievementMessagesConversationStarter
        };
    }

    componentDidMount() {
        if (this.props.notification.target && this.props.notification.target.comment &&
            this.props.notification.target.comment.mentionUser && !(this.props.users && this.props.users[this.props.notification.target.comment.mentionUser])) {
            this.props.usersConnector.fetchUser(this.props.notification.target.comment.mentionUser);
        }

        this.sendContentView();
    }

    componentDidUpdate() {
        this.sendContentView();
    }

    closeHandler() {
        this.props.usersConnector.markActivityAsRead(this.props.notification.id, true);
    }

    getImageFromNotification(notification: ActivityData) {
        switch (notification.type) {
            case enums.activityType.follow:
                return notification.user && notification.user.avatarUrl || placeholderImages.avatar;

            case enums.activityType.badge:
                return activityImages.trophy;

            case enums.activityType.reply:
                return notification.user && notification.user.avatarUrl || placeholderImages.avatar;

            case enums.activityType.reaction:
                return placeholderImages.peopleAvatar;
        }
    }

    getTitleFromNotification(notification: ActivityData, t: SocialLocalizedStrings, theme: string, componentConnector: ComponentConnector) {
        switch (notification.type) {
            case enums.activityType.follow:
                return <div dangerouslySetInnerHTML={{ __html: String.Format(t.NotificationFollowMessage,
                    {
                        userLink: renderToString(<a className="cwt-link" {...openProfilePage(componentConnector, theme, notification.user.id, this.props.market, "_blank") }> { notification.user.name } </a>)
                    })
                }} />;

            case enums.activityType.badge:
                return NotificationCard.AchievementTitles[notification.badgeType];

            case enums.activityType.reply:
                return <div dangerouslySetInnerHTML={{ __html: String.Format(t.NotificationReplyMessage,
                    {
                        userLink: renderToString(<a className="cwt-link" {...openProfilePage(componentConnector, theme, notification.user.id, this.props.market, "_blank")}>{ notification.user.name }</a>),
                        commentLink: renderToString(<a className="cwt-link" {...openArticlePage(theme, notification.target.url, notification.target.comment.contentId, notification.target.comment.id, "_blank")}>{ t.NotificationYourComment }</a>)
                    })
                }}/>;

            case enums.activityType.reaction: {
                let totalReactionCount = 0;
                if (notification.target.reactionCounts) {
                    totalReactionCount = Object.values(notification.target.reactionCounts).reduce(($sum, $count) => $sum + $count, 0);
                }
                return <div dangerouslySetInnerHTML={{ __html: String.Format(t.NotificationReactionMessage,
                    {
                        totalReactionCount: totalReactionCount,
                        commentLink: renderToString(<a className="cwt-link" {...openArticlePage(theme, notification.target.url, notification.target.comment.contentId, notification.target.comment.id, "_blank")}>{ t.NotificationYourComment }</a>)
                    })
                }}/>;
            }
        }
    }

    getMessageFromNotification(notification: ActivityData, users: { [id: string]: FollowInfo}, t: SocialLocalizedStrings, theme: string, componentConnector: ComponentConnector, market: string) {
        switch (notification.type) {
            case enums.activityType.follow:
                return "";

            case enums.activityType.badge:
                return <div className="cwt-clamp">
                    {NotificationCard.AchievementMessages[notification.badgeType]}
                </div>;

            case enums.activityType.reply: {
                const mentionUser = notification.target.comment.mentionUser;
                return notification.target.comment.isDeleted ?
                    <div className="cwt-clamp">
                        <span className="cwt-link cwt-comment-deleted-message" role="button" title={t.DisabledMessagesGuidelines} {...openGuidelinePage(componentConnector, theme, market, "_blank")}>
                            {t[enums.visibleStatusMap[notification.target.comment.visibleStatus] || enums.visibleStatusMap.Default]}
                            <i className="cwt-icon cwt-disabled-message">{exclamation_round}</i>
                        </span>
                    </div>
                    :
                    <div className="cwt-clamp">
                        {mentionUser &&
                            <React.Fragment>
                                <a className="cwt-name cwt-link" {...openProfilePage(componentConnector, theme, mentionUser, this.props.market, "_blank")}>{users && users[mentionUser] && users[mentionUser].data && users[mentionUser].data.name}</a>
                                &nbsp;
                            </React.Fragment>
                        }
                        <a
                            className="cwt-body-link"
                            {...openArticlePage(theme, notification.target.url, notification.target.comment.contentId, notification.target.comment.id, "_blank")}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.messageLink, false)}
                        >
                            <div dangerouslySetInnerHTML={{__html: notification.target.body}} />
                        </a>
                    </div>;
            }

            case enums.activityType.reaction: {
                let totalReactionCount = 0;
                if (notification.target.reactionCounts) {
                    totalReactionCount = Object.values(notification.target.reactionCounts).reduce(($sum, $count) => $sum + $count, 0);
                }
                return <div dangerouslySetInnerHTML={{ __html: String.Format(t.NotificationReactionMessage,
                    {
                        totalReactionCount: totalReactionCount,
                        commentLink: renderToString(<a className="cwt-link" {...openArticlePage(theme, notification.target.url, notification.target.comment.contentId, notification.target.comment.id, "_blank")}>{ t.NotificationYourComment }</a>)
                    })
                }}/>;
            }
        }
    }

    render() {
        const {componentConnector, users, notification, managedClasses, t, theme, market} = this.props;
        const isImageClickable = notification.type === enums.activityType.follow || notification.type === enums.activityType.reply;
        const isAchievement = notification.type === enums.activityType.badge;

        this.generateTelemetryObjects();

        const notificationImg = (
            <img
                alt={!isAchievement ? t.AriaLabelsProfilePicture : t.AriaLabelsAchievementTrophy}
                className={classNames("cwt-avatar", ["cwt-not-clickable", !isImageClickable], ["cwt-not-round", isAchievement])}
                src={this.getImageFromNotification(notification)}
                onError={(e)=>{ avatarFallback(e); }}
            />
        );

        return (
            <div className={managedClasses.notificationCardContainer} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.container, true, (el) => { this.containerRef = el; })}>
                <button
                    className="cwt-close-button cwt-link"
                    aria-label={t.AriaLabelsCloseNotification}
                    onClick={this.closeHandler.bind(this)}
                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.closeButton, false)}
                >
                    <i className="cwt-icon" > {close} </i>
                </button>
                <header className="cwt-notification-header">
                    <div className="cwt-image-container">
                        { isImageClickable ?
                            <a {...openProfilePage(componentConnector, theme, notification.user.id, market, "_blank")}>{ notificationImg }</a>
                            : notificationImg
                        }
                    </div>
                    <h6 className="cwt-title">{this.getTitleFromNotification(notification, t, theme, componentConnector)}</h6>
                </header>
                <div className={"cwt-message"}>{this.getMessageFromNotification(notification, users, t, theme, componentConnector, market)}</div>
            </div>
        );
    }

    generateTelemetryObjects() {
        const { notification, mainTelemetryObject } = this.props;
        if (!mainTelemetryObject) { return; }

        const containerTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, this.getContainerTelemetryContract(notification.type), { id: notification.id }, this.telemetryObjects && this.telemetryObjects.container);
        this.telemetryObjects = {
            container: containerTelemetryObject,
            closeButton: addOrUpdateTelemetryObject(containerTelemetryObject, notificationCardTelemetryContracts.closeButton, { id: notification.id }, this.telemetryObjects && this.telemetryObjects.closeButton),
            messageLink: addOrUpdateTelemetryObject(containerTelemetryObject, this.getMessageLinkTelemetryContract(notification.type), { id: notification.id }, this.telemetryObjects && this.telemetryObjects.messageLink)
        };
    }

    getContainerTelemetryContract(notificationType) {
        switch (notificationType) {
            case enums.activityType.badge: return notificationCardTelemetryContracts.achievement;
            case enums.activityType.reply: return notificationCardTelemetryContracts.reply;
        }
    }

    getMessageLinkTelemetryContract(notificationType) {
        switch (notificationType) {
            case enums.activityType.badge: return notificationCardTelemetryContracts.achievementLink;
            case enums.activityType.reply: return notificationCardTelemetryContracts.commentLink;
        }
    }

    sendContentView() {
        if (!this.containerRef || this.isLoaded) { return; }

        this.isLoaded = true;
        dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.containerRef.parentElement);
    }
}